export const dataSchemas = {
  locations: {
    schema: `{
      "locations": [
        {
          "name": "Place name",
          "city": "City name",
          "country": "Country name",
          "position": {
            "lat": 0.0,
            "lng": 0.0
          },
          "rating": 4.5,
          "reviews": 1200,
          "type": "museum|art-gallery|restaurant|cafe|bar|nightclub|hotel|hostel|resort|park|garden|beach|monument|temple|church|mosque|synagogue|palace|castle|fort|bridge|tower|viewpoint|market|shopping-mall|store|theater|cinema|concert-hall|stadium|amusement-park|theme-park|zoo|aquarium|observatory|planetarium|library|university|historic-site|archaeological-site|memorial|cemetery|spa|casino|golf-course|ski-resort|marina|pier|lighthouse|waterfall|cave|mountain|lake|island|vineyard|winery|brewery|distillery|farm|ranch|botanical-garden|national-park|nature-reserve|hiking-trail|adventure-sport|cultural-center|festival-ground|street|square|neighborhood|district"
        }
      ]
    }`,
    example: {
    locations: [{
      name: "Brandenburg Gate",
      city: "Berlin",
      country: "Germany",
      position: {
        lat: 52.5163,
        lng: 13.3777
      },
      rating: 4.7,
      reviews: 50000,
      type: "historic-site"
    }]
  }
  },

  flight: {
    schema: `{
      "flightNumber": "string",
      "airline": "string", 
      "flightCode": "string",
      "status": "scheduled|boarding|departed|in-flight|landed|cancelled|delayed",
      "departure": {
        "airport": "string",
        "airportCode": "string",
        "city": "string",
        "terminal": "string",
        "gate": "string",
        "scheduledTime": "ISO 8601 datetime",
        "actualTime": "ISO 8601 datetime",
        "delay": "number (minutes)"
      },
      "arrival": {
        "airport": "string",
        "airportCode": "string", 
        "city": "string",
        "terminal": "string",
        "gate": "string",
        "scheduledTime": "ISO 8601 datetime",
        "estimatedTime": "ISO 8601 datetime",
        "actualTime": "ISO 8601 datetime",
        "delay": "number (minutes)"
      },
      "duration": "string",
      "aircraft": "string",
      "progress": "number (0-100)"
    }`,
    example: null
  },

  visa: {
    schema: `{
      "visaInfo": {
        "origin": "Country of passport",
        "destination": "Destination country",
        "visaRequired": true|false,
        "visaType": "tourist|business|transit|eVisa|on-arrival",
        "duration": "30 days|90 days|6 months",
        "cost": {
          "amount": 100,
          "currency": "USD"
        },
        "processingTime": "3-5 business days",
        "requirements": ["Valid passport", "Return ticket", "Hotel booking"],
        "notes": "Additional important information"
      }
    }`,
    example: null
  },

  budget: {
    schema: `{
      "budget": {
        "destination": "City/Country",
        "duration": "5 days",
        "totalEstimate": {
          "min": 1000,
          "max": 1500,
          "currency": "USD"
        },
        "breakdown": {
          "accommodation": { "min": 300, "max": 500, "perNight": 60 },
          "food": { "min": 200, "max": 300, "perDay": 40 },
          "transportation": { "min": 150, "max": 250 },
          "attractions": { "min": 200, "max": 300 },
          "shopping": { "min": 150, "max": 200 }
        },
        "budgetLevel": "budget|mid-range|luxury",
        "tips": ["Book accommodation in advance", "Use public transport"]
      }
    }`,
    example: null
  },

  weather: {
    schema: `{
      "weather": {
        "location": "City name",
        "current": {
          "temperature": 25,
          "condition": "sunny|cloudy|rainy|snowy",
          "humidity": 65,
          "windSpeed": 15
        },
        "forecast": [
          {
            "day": "Mon",
            "date": "2024-06-13",
            "high": 28,
            "low": 22,
            "condition": "sunny|cloudy|rainy|snowy"
          }
        ],
        "bestTimeToVisit": "March-May, September-November"
      }
    }`,
    example: null
  },

  festival: {
    schema: `{
      "festivals": [
        {
          "name": "Festival name",
          "location": "City, Country",
          "dates": {
            "start": "2024-10-01",
            "end": "2024-10-10"
          },
          "type": "cultural|religious|music|food|seasonal",
          "description": "Brief description",
          "highlights": ["Main events", "Key attractions"],
          "tips": ["Book early", "Dress modestly"]
        }
      ]
    }`,
    example: null
  },

  currency: {
    schema: `{
      "currency": {
        "from": {
          "code": "USD",
          "name": "US Dollar",
          "symbol": "$",
          "amount": 100
        },
        "to": {
          "code": "EUR",
          "name": "Euro",
          "symbol": "€",
          "amount": 92.5
        },
        "rate": 0.925,
        "lastUpdated": "2024-06-13T12:00:00Z",
        "trend": "up|down|stable"
      }
    }`,
    example: null
  },

  language: {
    schema: `{
      "language": {
        "destination": "Country/Region",
        "primaryLanguage": "Spanish",
        "otherLanguages": ["English", "Catalan"],
        "phrases": [
          {
            "english": "Hello",
            "local": "Hola",
            "pronunciation": "OH-lah"
          },
          {
            "english": "Thank you",
            "local": "Gracias",
            "pronunciation": "GRAH-see-ahs"
          }
        ],
        "tips": ["English widely spoken in tourist areas", "Learn basic greetings"]
      }
    }`,
    example: null
  },

  advisory: {
    schema: `{
      "advisory": {
        "destination": "Country/City",
        "level": "safe|caution|reconsider|avoid",
        "lastUpdated": "2024-06-13",
        "warnings": [
          {
            "type": "health|security|natural|political",
            "severity": "low|medium|high",
            "description": "Details of the warning",
            "affectedAreas": ["Specific regions"]
          }
        ],
        "recommendations": ["Register with embassy", "Get travel insurance"],
        "emergencyContacts": {
          "police": "112",
          "medical": "112",
          "embassy": "+1-234-567-8900"
        }
      }
    }`,
    example: null
  },

  restaurant: {
    schema: `{
      "restaurant": [
        {
          "name": "Restaurant name",
          "cuisine": "Italian|French|Asian|etc",
          "priceRange": "$|$$|$$$|$$$$",
          "rating": 4.5,
          "reviews": 500,
          "location": {
            "address": "123 Main St",
            "city": "Paris",
            "coordinates": [latitude, longitude]
          },
          "reservationNeeded": true,
          "highlights": ["Michelin star", "Rooftop dining"],
          "bestFor": "romantic dinner|business lunch|family",
          "hours": {
            "lunch": "12:00-14:30",
            "dinner": "19:00-22:30"
          }
        }
      ]
    }`,
    example: null
  }
};

// Helper function to get schema string for LLM prompts
export function getSchemaForType(type: string): string {
  return dataSchemas[type]?.schema || '';
}

// Type definitions for TypeScript
export interface LocationData {
  name: string;
  city: string;
  country: string;
  coordinates: [number, number];
  rating: number;
  reviews: number;
  description?: string;
  type?: string;
}

export interface FlightData {
  flightNumber: string;
  airline: string;
  status: 'on-time' | 'delayed' | 'cancelled' | 'boarding' | 'departed' | 'arrived';
  departure: {
    airport: string;      // 3-letter code (e.g., "MUC")
    city: string;         // City name (e.g., "Munich")
    terminal: string;
    gate: string;
    scheduledTime: string;
    actualTime: string;
  };
  arrival: {
    airport: string;      // 3-letter code (e.g., "JFK")
    city: string;         // City name (e.g., "New York")
    terminal: string;
    gate: string;
    scheduledTime: string;
    estimatedTime: string;
  };
  duration: string;
  aircraft: string;
}

// Add other interfaces as needed...